<template>
  <div v-if="showBlock" class="available">
    <div class="d-flex align-center mb-3">
      <h3 class="available__title">Доступные к покупке</h3>
      <v-spacer />
      <a v-if="items.length > limit" class="text-primary caption" @click="expanded = !expanded">
        {{ expanded ? 'Свернуть' : `Показать все (${items.length}) →` }}
      </a>
    </div>

    <StateBlock v-if="loading && !items.length" state="loading" compact />
    <StateBlock v-else-if="error" state="error" compact @retry="load" />
    <div v-else class="available__grid">
      <div v-for="item in shown" :key="item.card.id" class="available-card">
        <div class="available-card__cover" @click="open(item.card.id)">
          <v-img v-if="item.card.main_image_url" :src="item.card.main_image_url" height="90" contain />
          <v-icon v-else size="44" color="grey lighten-1">mdi-clipboard-text-outline</v-icon>
        </div>
        <div class="available-card__body">
          <div class="available-card__name" @click="open(item.card.id)">{{ item.card.name }}</div>
          <div class="available-card__desc">{{ item.card.short_description }}</div>

          <div class="available-card__actions">
            <template v-if="item.state === 'in_cart'">
              <BasketCounter :id="item.card.id" />
              <v-btn small text color="primary" :to="{ name: 'basket' }">В корзину →</v-btn>
            </template>
            <template v-else-if="item.state === 'pending'">
              <span class="available-card__badge">Ожидает оплаты</span>
              <v-btn small outlined color="primary" :to="{ name: 'my-orders' }">Мои заказы</v-btn>
            </template>
            <template v-else>
              <div class="available-card__stepper">
                <v-btn icon x-small :disabled="quantityOf(item.card.id) <= 1" @click="changeQuantity(item.card.id, -1)">
                  <v-icon small>mdi-minus</v-icon>
                </v-btn>
                <span class="available-card__qty">{{ quantityOf(item.card.id) }}</span>
                <v-btn icon x-small @click="changeQuantity(item.card.id, 1)">
                  <v-icon small>mdi-plus</v-icon>
                </v-btn>
              </div>
              <v-btn small color="primary" :loading="addingId === item.card.id" @click="addToCart(item.card.id)">
                <v-icon left small>mdi-cart-plus</v-icon> В корзину
              </v-btn>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BasketCounter from '@/components/basket/BasketCounter.vue';
import StateBlock from '@/components/ui/StateBlock.vue';
import {
  STATE_ORDER, loadOrderedCardIds, walletBalanceOf, tileState,
} from '@/components/list-services/catalogHelpers';

// Услуги из каталога, которых у компании ещё нет: корп видит их в «Обзоре» рядом с остатками и кладёт в корзину,
// не переходя в каталог. Состояния плиток считаются теми же функциями, что и в каталоге.
const BUYABLE_STATES = ['pending', 'in_cart', 'not_bought'];

export default {
  name: 'AvailableServices',
  components: { BasketCounter, StateBlock },
  props: {
    wallet: { type: Object, default: null },
    // Сколько карточек показываем в свёрнутом виде
    limit: { type: Number, default: 4 },
  },
  data() {
    return {
      expanded: false,
      loading: false,
      error: false,
      paidCardIds: [],
      pendingCardIds: [],
      quantities: {},
      addingId: null,
    };
  },
  computed: {
    ...mapGetters({
      services: 'services/services/GET_ALL_SERVICES',
      itemInBasket: 'basket/basket/itemInBasket',
    }),
    publishedCards() {
      return (this.services || []).filter((s) => s.status === 'published' && s.is_visible !== false);
    },
    items() {
      // Без сводки кошелька остатки неизвестны — ничего не показываем, чтобы не предлагать уже купленное
      if (!this.wallet) return [];
      return this.publishedCards
        .map((card) => {
          const { balance, known } = walletBalanceOf(card, this.wallet);
          const state = tileState({
            isAdmin: false,
            isManager: false,
            inBasket: !!this.itemInBasket(card.id),
            isTest: card.nomenclature != null,
            balance,
            known,
            paid: this.paidCardIds.includes(card.id),
            pending: this.pendingCardIds.includes(card.id),
          });
          return { card, state };
        })
        .filter((t) => BUYABLE_STATES.includes(t.state))
        .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state));
    },
    shown() {
      return this.expanded ? this.items : this.items.slice(0, this.limit);
    },
    // Если покупать нечего (всё куплено), блок не рисуем
    showBlock() {
      return this.loading || this.error || this.items.length > 0;
    },
  },
  methods: {
    ...mapActions({
      loadServices: 'services/services/GET_ALL_SERVICES',
      addToBasket: 'basket/basket/addToBasket',
      fetchUserBasket: 'basket/basket/fetchUserBasket',
    }),
    quantityOf(id) {
      return this.quantities[id] || 1;
    },
    changeQuantity(id, delta) {
      this.$set(this.quantities, id, Math.max(1, this.quantityOf(id) + delta));
    },
    open(id) {
      this.$router.push({ name: 'serviceDetail', params: { id } });
    },
    async load() {
      this.loading = true;
      this.error = false;
      try {
        // Корзина нужна, чтобы увидеть, что уже лежит в ней
        await Promise.all([this.loadServices(), this.fetchUserBasket().catch(() => {})]);
        // Без заказов состояние «куплена / ожидает оплаты» неизвестно — вместо блока покажем ошибку с повтором
        const { paid, pending } = await loadOrderedCardIds();
        this.paidCardIds = paid;
        this.pendingCardIds = pending;
      } catch (e) {
        this.error = true;
      } finally {
        this.loading = false;
      }
    },
    async addToCart(id) {
      if (this.addingId) return;
      this.addingId = id;
      try {
        await this.addToBasket({ service_card_id: id, quantity: this.quantityOf(id) });
        await this.fetchUserBasket();
      } catch (e) {
        this.$toast.error('Не удалось добавить в корзину', '', { position: 'topRight' });
      } finally {
        this.addingId = null;
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped lang="scss">
.available {
  margin-top: 24px;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #fff;

  &__title {
    font-size: 16px;
    font-weight: 600;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 16px;
  }
}

.available-card {
  display: flex;
  flex-direction: column;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;

  &__cover {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100px;
    background: #f7f8fa;
    cursor: pointer;
  }

  &__body {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 4px;
    padding: 12px 14px;
  }

  &__name {
    font-weight: 600;
    font-size: 15px;
    color: #212121;
    cursor: pointer;
  }

  &__desc {
    font-size: 13px;
    color: #757575;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: auto;
    padding-top: 8px;
  }

  &__stepper {
    display: flex;
    align-items: center;
    gap: 2px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    padding: 0 2px;
  }

  &__qty {
    min-width: 22px;
    text-align: center;
    font-size: 14px;
  }

  &__badge {
    padding: 1px 8px;
    border-radius: 10px;
    font-size: 11px;
    background: #fff3e0;
    color: #e65100;
  }
}
</style>
